import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useLanguage } from '@/hooks/useLanguage'
import type { ExploreSort } from '../catalogApi'
import type { CourseDifficulty } from '@/types/course'
import type { LangFilter } from '@/types/lang'

export interface CatalogParams {
  page: number
  search: string
  categoriesFilter: number[]
  difficultyFilter: CourseDifficulty[]
  languageFilter: LangFilter
  sort: ExploreSort
}

const DIFFICULTIES: readonly CourseDifficulty[] = ['Easy', 'Medium', 'Hard']
const LANG_FILTERS: readonly string[] = ['ru', 'en', 'all']

const parseLangFilter = (raw: string | null, fallback: LangFilter): LangFilter =>
  raw && LANG_FILTERS.includes(raw) ? (raw as LangFilter) : fallback

const SORTS: readonly ExploreSort[] = ['popular', 'updated', 'completions']

const parseSort = (raw: string | null): ExploreSort =>
  (SORTS as readonly string[]).includes(raw ?? '') ? (raw as ExploreSort) : 'popular'

const parsePage = (raw: string | null): number => {
  const n = Number.parseInt(raw ?? '1', 10)
  return Number.isFinite(n) && n >= 1 ? n : 1
}

const parseCsvInts = (raw: string | null): number[] => {
  if (!raw) return []
  return raw
    .split(',')
    .map((s) => Number.parseInt(s, 10))
    .filter((n) => Number.isFinite(n))
}

const parseCsvDifficulty = (raw: string | null): CourseDifficulty[] => {
  if (!raw) return []
  return raw
    .split(',')
    .filter((v): v is CourseDifficulty => (DIFFICULTIES as readonly string[]).includes(v))
}

export const useCatalogParams = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { language } = useLanguage()

  const params = useMemo<CatalogParams>(
    () => ({
      page: parsePage(searchParams.get('page')),
      search: searchParams.get('search') ?? '',
      categoriesFilter: parseCsvInts(searchParams.get('categoriesFilter')),
      difficultyFilter: parseCsvDifficulty(searchParams.get('difficultyFilter')),
      languageFilter: parseLangFilter(searchParams.get('languageFilter'), language),
      sort: parseSort(searchParams.get('sort')),
    }),
    [searchParams, language],
  )

  const update = useCallback(
    (patch: Partial<CatalogParams>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        const changesFilter =
          'search' in patch ||
          'categoriesFilter' in patch ||
          'difficultyFilter' in patch ||
          'languageFilter' in patch ||
          'sort' in patch

        if (changesFilter) next.delete('page')

        if (patch.page !== undefined) {
          if (patch.page > 1) next.set('page', String(patch.page))
          else next.delete('page')
        }
        if (patch.search !== undefined) {
          if (patch.search) next.set('search', patch.search)
          else next.delete('search')
        }
        if (patch.categoriesFilter !== undefined) {
          if (patch.categoriesFilter.length > 0)
            next.set('categoriesFilter', patch.categoriesFilter.join(','))
          else next.delete('categoriesFilter')
        }
        if (patch.difficultyFilter !== undefined) {
          if (patch.difficultyFilter.length > 0)
            next.set('difficultyFilter', patch.difficultyFilter.join(','))
          else next.delete('difficultyFilter')
        }
        if (patch.languageFilter !== undefined) {
          if (patch.languageFilter !== language) next.set('languageFilter', patch.languageFilter)
          else next.delete('languageFilter')
        }
        if (patch.sort !== undefined) {
          if (patch.sort !== 'popular') next.set('sort', patch.sort)
          else next.delete('sort')
        }
        return next
      })
    },
    [setSearchParams, language],
  )

  // Находка 3: «сброс» обязан явно выражать состояние «без языкового фильтра» —
  // это 'all', а не «язык интерфейса» (в который резолвится отсутствие
  // параметра). setSearchParams({}) сбрасывал бы languageFilter в implicit-
  // состояние языка интерфейса, а не в 'all' — та же самая пустая выдача.
  const reset = useCallback(() => setSearchParams({ languageFilter: 'all' }), [setSearchParams])

  // Находка 4: смена языка интерфейса резолвит languageFilter в новый язык в
  // обход update(), поэтому page не сбрасывается сам по себе — «страница 5»
  // русских курсов остаётся в URL, хотя английских курсов там может не быть.
  // Срабатывает ТОЛЬКО когда languageFilter не задан в URL явно (implicit
  // default = язык интерфейса) — явный языковой фильтр из ссылки не должен
  // терять свою страницу при смене интерфейса, он от неё не зависит.
  const prevInterfaceLanguage = useRef(language)
  useEffect(() => {
    if (prevInterfaceLanguage.current === language) return
    prevInterfaceLanguage.current = language
    const explicitLanguageFilter = searchParams.get('languageFilter') !== null
    if (explicitLanguageFilter) return
    if (parsePage(searchParams.get('page')) <= 1) return
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('page')
      return next
    })
  }, [language, searchParams, setSearchParams])

  return { ...params, update, reset }
}
